import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CircleAlert, Mic, PlugZap, Power, Sparkles, Square, Wallet } from 'lucide-react';
import { systemClock } from '@reos/config';
import { useT } from '../lib/i18n.js';
import { getTenantSettings } from '../lib/assistant-settings-api.js';
import { getCreditBalance } from '../lib/ai-credits-api.js';
import { getAsistentkaBriefing, getAsistentkaChecks, type AsistentkaFinding } from '../lib/asistentka-api.js';
import {
  AssistantTranscribeError,
  sendAssistantChat,
  transcribeAssistantAudio,
  type AssistantChatResult,
} from '../lib/asistentka-chat-api.js';
import { browserRecorder, type Recorder, type RecorderHandle } from '../lib/field-intake-recorder.js';
import {
  failedChecks,
  findingPath,
  findingRef,
  hiddenTaskCount,
  paymentState,
  surfaceTasks,
  waitingOn,
} from '../lib/asistentka-surface.js';
import { formatFullDate } from '../lib/relative-time.js';
import { V4Group, V4NextStep, V4Pill, V4Row } from '../components/ux-v4.js';
import { PageHead } from '../components/page-head.js';

/** Nahravac jde v testu nahradit atrapou — stejny vzor jako `nabor.tsx` (UXV4c). */
export interface AsistentkaPageProps {
  readonly recorder?: Recorder | undefined;
}

/**
 * `/asistentka` (krok AS3 bod 5, kucharka krok 5) — plocha Asistentky.
 *
 *  1. STAV neutralne: kdo plati AI (KR1) — vypnuto / vlastni klic / kredity N Kč.
 *  2. Vypnuto nebo nula kreditu → JEDNA vec „Další krok": kde to zapnout.
 *     Plocha pak core vubec NEVOLA (Asistentka nic nevola — zadani krok 0).
 *  3. Core nedostupne → „Asistentka zatím není připojená“, ne chybova obrazovka.
 *  4. Pripojena: ranni prehled, 3–5 ukolu (high prvni), „Čekáme na koho“.
 *
 * Plocha nic neodesila a nic nezapisuje — jen cte (proxy pusti jen GET).
 * Neprestavovat bez zpetne vazby uzivatele (kucharka krok 5).
 *
 * Krok NAP5 bod A11 — mikrofon v okne Asistentky. Nahrava TENTYZ
 * `browserRecorder`, jaky uz pouziva nabor na miste (`nabor.tsx`), a posila
 * nahravku na `POST /v1/assistant/transcribe`. Prepis se jen VLOZI do editoru —
 * poslat (a tim pripravit schvaleni) porad musi makler sam.
 */
export function AsistentkaPage({ recorder = browserRecorder }: AsistentkaPageProps): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [now] = React.useState(() => systemClock.now());

  const settings = useQuery({ queryKey: ['asistentka', 'settings'], queryFn: getTenantSettings });
  const payer = settings.data?.aiKeySource ?? null;
  const balance = useQuery({
    queryKey: ['asistentka', 'balance'],
    queryFn: getCreditBalance,
    enabled: payer === 'app_credits',
  });
  const state = payer === null ? null : paymentState(payer, balance.data?.balanceCzk ?? null);
  const waitingForBalance = payer === 'app_credits' && balance.data === undefined;
  const coreAllowed =
    state !== null && !waitingForBalance && (state.kind === 'tenant_key' || (state.kind === 'app_credits' && state.balanceCzk > 0));

  const briefing = useQuery({ queryKey: ['asistentka', 'briefing'], queryFn: getAsistentkaBriefing, enabled: coreAllowed });
  const checks = useQuery({ queryKey: ['asistentka', 'checks'], queryFn: getAsistentkaChecks, enabled: coreAllowed });

  const statusText =
    state === null
      ? ''
      : state.kind === 'off'
        ? t('webAdmin.asistentka.status.off')
        : state.kind === 'tenant_key'
          ? t('webAdmin.asistentka.status.tenantKey')
          : t('webAdmin.asistentka.status.credits', { amount: new Intl.NumberFormat('cs-CZ').format(state.balanceCzk) });
  const lede =
    state === null
      ? undefined
      : t(state.kind === 'off' ? 'webAdmin.asistentka.lede.off' : state.kind === 'tenant_key' ? 'webAdmin.asistentka.lede.tenantKey' : 'webAdmin.asistentka.lede.credits');

  const toSettings = (): void => void navigate({ to: '/nastaveni' });
  const open = (path: string | null): (() => void) | undefined =>
    path === null ? undefined : () => void navigate({ to: path as never });

  const [chatText, setChatText] = React.useState('');
  const [chatBusy, setChatBusy] = React.useState(false);
  const [chatResult, setChatResult] = React.useState<AssistantChatResult | 'error' | null>(null);

  const sendChat = async (): Promise<void> => {
    const text = chatText.trim();
    if (text === '' || chatBusy) return;
    setChatBusy(true);
    setChatResult(null);
    try {
      const result = await sendAssistantChat(text);
      setChatResult(result);
      if (result.status === 'prepared') setChatText('');
    } catch {
      setChatResult('error');
    } finally {
      setChatBusy(false);
    }
  };

  /*
   * Krok NAP5 bod A11 — diktat. Stav je jen „nahravam / prepisuji / chyba";
   * hlasku o nenasazene sluzbe prevezmeme ze serveru (uz je lokalizovana,
   * `assistant.voice.not_configured`), aby UI netvrdilo neco jineho nez API.
   */
  const [micState, setMicState] = React.useState<'idle' | 'recording' | 'transcribing'>('idle');
  const [micError, setMicError] = React.useState<string | null>(null);
  const micHandle = React.useRef<RecorderHandle | null>(null);

  const startDictation = async (): Promise<void> => {
    if (micState !== 'idle') return;
    setMicError(null);
    try {
      micHandle.current = await recorder.start();
      setMicState('recording');
    } catch {
      // Zamitnuty mikrofon neni chyba aplikace — rekne se, kde ho povolit.
      setMicError(t('webAdmin.asistentka.chat.micDenied'));
      setMicState('idle');
    }
  };

  const stopDictation = async (): Promise<void> => {
    const handle = micHandle.current;
    if (handle === null || micState !== 'recording') return;
    micHandle.current = null;
    setMicState('transcribing');
    try {
      const { blob } = await handle.stop();
      const text = await transcribeAssistantAudio(blob);
      // Diktat PRIDAVA k rozepsanemu textu, nikdy ho neprepisuje.
      setChatText((current) => (current.trim() === '' ? text : `${current.trim()} ${text}`));
    } catch (error) {
      setMicError(
        error instanceof AssistantTranscribeError && error.detail !== null
          ? error.detail
          : t('webAdmin.asistentka.chat.micError'),
      );
    } finally {
      setMicState('idle');
    }
  };

  const connected = briefing.data?.connected === true && checks.data?.connected === true;
  const notConnected = coreAllowed && (briefing.data?.connected === false || checks.data?.connected === false);
  const findings: readonly AsistentkaFinding[] = checks.data?.connected === true ? checks.data.data.findings : [];
  const tasks = surfaceTasks(findings);
  const more = hiddenTaskCount(findings);
  const waiting = waitingOn(findings);
  const failed = failedChecks(findings);

  return (
    <div className="rh-v4-page" data-testid="asistentka-page">
      <PageHead
        variant="plain"
        breadcrumb={formatFullDate(now)}
        title={t('webAdmin.asistentka.title')}
        titleTestId="asistentka-title"
        subtitle={lede}
      />

      <V4Group label={t('webAdmin.asistentka.payerLabel')} testId="asistentka-status">
        <V4Row
          icon={
            <span className="rh-v4-ico rh-v4-ico--grey" aria-hidden="true">
              <Wallet size={16} strokeWidth={2} />
            </span>
          }
          title={t('webAdmin.asistentka.payerLabel')}
          value={<span data-testid="asistentka-status-value">{statusText}</span>}
          chevron
          onClick={toSettings}
        />
      </V4Group>

      {state?.kind === 'off' ? (
        <V4NextStep
          testId="asistentka-off"
          kicker={t('webAdmin.asistentka.off.kicker')}
          icon={<Power size={16} strokeWidth={2} />}
          title={t('webAdmin.asistentka.off.title')}
          note={t('webAdmin.asistentka.off.note')}
          actions={
            <button type="button" className="rh-v4-btn rh-v4-btn--filled" onClick={toSettings} data-testid="asistentka-open-settings">
              {t('webAdmin.asistentka.off.action')}
            </button>
          }
        />
      ) : null}

      {state?.kind === 'app_credits' && !waitingForBalance && state.balanceCzk <= 0 ? (
        <V4NextStep
          testId="asistentka-no-credits"
          kicker={t('webAdmin.asistentka.noCredits.kicker')}
          icon={<Wallet size={16} strokeWidth={2} />}
          title={t('webAdmin.asistentka.noCredits.title')}
          note={t('webAdmin.asistentka.noCredits.note')}
          actions={
            <button type="button" className="rh-v4-btn rh-v4-btn--filled" onClick={toSettings}>
              {t('webAdmin.asistentka.noCredits.action')}
            </button>
          }
        />
      ) : null}

      {notConnected ? (
        <V4NextStep
          testId="asistentka-not-connected"
          kicker={t('webAdmin.asistentka.notConnected.kicker')}
          icon={<PlugZap size={16} strokeWidth={2} />}
          title={t('webAdmin.asistentka.notConnected.title')}
          note={t('webAdmin.asistentka.notConnected.note')}
          actions={null}
        />
      ) : null}

      {connected ? (
        <>
          <h2 className="rh-v4-h2">{t('webAdmin.asistentka.briefing.title')}</h2>
          <V4Group testId="asistentka-briefing">
            <V4Row
              icon={
                <span className="rh-v4-ico" aria-hidden="true">
                  <Sparkles size={16} strokeWidth={2} />
                </span>
              }
              title={
                <span className="rh-asist-briefing">
                  {briefing.data?.connected === true && briefing.data.data !== null && briefing.data.data.text !== ''
                    ? briefing.data.data.text
                    : t('webAdmin.asistentka.briefing.empty')}
                </span>
              }
            />
          </V4Group>

          {failed.length > 0 ? (
            <V4Group testId="asistentka-failed">
              <V4Row
                icon={
                  <span className="rh-v4-ico rh-v4-ico--warn" aria-hidden="true">
                    <CircleAlert size={16} strokeWidth={2} />
                  </span>
                }
                title={t('webAdmin.asistentka.failed.title')}
                sub={failed.map((f) => f.title).join(' · ')}
              />
            </V4Group>
          ) : null}

          <h2 className="rh-v4-h2">{t('webAdmin.asistentka.tasks.title')}</h2>
          <V4Group testId="asistentka-tasks">
            {tasks.length === 0 ? (
              <V4Row title={t('webAdmin.asistentka.tasks.empty')} testId="asistentka-tasks-empty" />
            ) : (
              tasks.map((f) => (
                <V4Row
                  key={`${f.code}:${f.key}`}
                  testId={`asistentka-task-${f.key}`}
                  title={f.title}
                  sub={`${findingRef(f)} · ${f.fix}`}
                  right={
                    <V4Pill tone={f.severity === 'high' ? 'warn' : f.severity === 'medium' ? 'accent' : 'neutral'}>
                      {t(`webAdmin.asistentka.urgency.${f.urgency}` as 'webAdmin.asistentka.urgency.today')}
                    </V4Pill>
                  }
                  chevron={findingPath(f) !== null}
                  onClick={open(findingPath(f))}
                />
              ))
            )}
            {more > 0 ? <V4Row title={t('webAdmin.asistentka.tasks.more', { count: more })} testId="asistentka-tasks-more" /> : null}
          </V4Group>

          <h2 className="rh-v4-h2">{t('webAdmin.asistentka.waiting.title')}</h2>
          <V4Group testId="asistentka-waiting">
            {waiting.length === 0 ? (
              <V4Row title={t('webAdmin.asistentka.waiting.empty')} testId="asistentka-waiting-empty" />
            ) : (
              waiting.map((f) => (
                <V4Row
                  key={`${f.code}:${f.key}`}
                  testId={`asistentka-waiting-${f.key}`}
                  title={t('webAdmin.asistentka.waiting.row', {
                    who: t(`webAdmin.asistentka.waiting.${f.waitingFor ?? 'klient'}` as 'webAdmin.asistentka.waiting.klient'),
                  })}
                  sub={`${findingRef(f)} · ${f.title}`}
                  chevron={findingPath(f) !== null}
                  onClick={open(findingPath(f))}
                />
              ))
            )}
          </V4Group>
        </>
      ) : null}

      <h2 className="rh-v4-h2">{t('webAdmin.asistentka.chat.title')}</h2>
      <V4Group testId="asistentka-chat">
        {/*
          Zapis pres parser NIKDY nebezi AI core (je to levny D-urovnovy
          rozbor, 0 Kc) — bezi i pri vypnutem/nedostupnem core, viz test
          „Zápis NIKDY neběží... i když je Asistentka (core) vypnutá".
          Audit UXK4 (35) proto NEMUZE zablokovat editor — misto toho pod
          nej pri nedostupnem core pridava staticke priklady, aby „Napište
          Asistentce" nepusobilo jako slib funkce core (ranni prehled atd).
        */}
        <div className="rh-asist-chat">
          <textarea
            className="rh-asist-chat__input"
            data-testid="asistentka-chat-input"
            placeholder={t('webAdmin.asistentka.chat.placeholder')}
            value={chatText}
            onChange={(e) => setChatText(e.target.value)}
            rows={2}
          />
          {/*
            Krok NAP5 bod A11: mikrofon je vedle „Poslat" — diktat jen PLNI
            editor, poslat musi makler sam. Prohlizec bez MediaRecorder
            tlacitko vubec neukaze (zadna falesna funkce).
          */}
          {recorder.supported ? (
            <button
              type="button"
              className={`rh-v4-btn${micState === 'recording' ? ' rh-v4-btn--filled' : ''}`}
              data-testid="asistentka-chat-mic"
              aria-pressed={micState === 'recording'}
              disabled={chatBusy || micState === 'transcribing'}
              onClick={() => void (micState === 'recording' ? stopDictation() : startDictation())}
            >
              {micState === 'recording' ? <Square size={16} strokeWidth={2} /> : <Mic size={16} strokeWidth={2} />}
              {micState === 'recording'
                ? t('webAdmin.asistentka.chat.micStop')
                : micState === 'transcribing'
                  ? t('webAdmin.asistentka.chat.micTranscribing')
                  : t('webAdmin.asistentka.chat.mic')}
            </button>
          ) : null}
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--filled"
            data-testid="asistentka-chat-send"
            disabled={chatBusy || micState !== 'idle' || chatText.trim() === ''}
            onClick={() => void sendChat()}
          >
            {chatBusy ? t('webAdmin.asistentka.chat.sending') : t('webAdmin.asistentka.chat.send')}
          </button>
        </div>
        {micState === 'recording' ? (
          <V4Row testId="asistentka-chat-mic-recording" title={t('webAdmin.asistentka.chat.micRecording')} />
        ) : null}
        {micError !== null ? <V4Row testId="asistentka-chat-mic-error" title={micError} /> : null}
        {chatResult === 'error' ? (
          <V4Row testId="asistentka-chat-error" title={t('webAdmin.asistentka.chat.error')} />
        ) : chatResult?.status === 'unknown' ? (
          <V4Row testId="asistentka-chat-unknown" title={t('webAdmin.asistentka.chat.unknown')} />
        ) : chatResult?.status === 'prepared' ? (
          <V4Row
            testId="asistentka-chat-prepared"
            title={t('webAdmin.asistentka.chat.preparedTitle')}
            sub={t('webAdmin.asistentka.chat.preparedNote')}
            chevron
            onClick={open('/schvaleni')}
          />
        ) : !connected ? (
          <ul className="rh-asist-chat__examples" data-testid="asistentka-chat-examples">
            <li className="rh-v4-head__lede">{t('webAdmin.asistentka.chat.examplesTitle')}</li>
            <li>{t('webAdmin.asistentka.chat.example1')}</li>
            <li>{t('webAdmin.asistentka.chat.example2')}</li>
            <li>{t('webAdmin.asistentka.chat.example3')}</li>
          </ul>
        ) : null}
      </V4Group>

      <p className="rh-v4-head__lede rh-asist-promise" data-testid="asistentka-promise">
        {t('webAdmin.asistentka.promise')}
      </p>
    </div>
  );
}
